/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MessagesList.tsx - Componente lista de mensajes
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Lista de mensajes con scroll infinito y paginación
 * @version 1.0.0
 * @architecture WASP Frontend
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useRef, useEffect } from 'react';
import { Spinner, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import MessageBubble, { Message } from './MessageBubble';

interface MessagesListProps {
  messages: Message[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  error?: string | null;
  onLoadMore?: () => void;
  autoScroll?: boolean;
  onScrollToBottom?: () => void;
  compact?: boolean;
}

/**
 * Componente MessagesList
 * 
 * Lista de mensajes con scroll automático y carga paginada
 * 
 * @example
 * ```tsx
 * <MessagesList
 *   messages={messages}
 *   loading={loading}
 *   hasMore={hasMoreMessages}
 *   onLoadMore={loadMoreMessages}
 *   autoScroll={true}
 * />
 * ```
 */
export const MessagesList: React.FC<MessagesListProps> = ({
  messages,
  loading = false,
  loadingMore = false,
  hasMore = false,
  error = null,
  onLoadMore,
  autoScroll = true,
  onScrollToBottom,
  compact = false
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef<number>(0);
  const [showScrollButton, setShowScrollButton] = React.useState(false);

  /**
   * Scroll automático al final cuando hay nuevos mensajes
   */
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  /**
   * Detectar si el usuario está cerca del final del scroll
   */
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    setShowScrollButton(!isNearBottom);
    lastScrollTop.current = scrollTop;
  };

  /**
   * Scroll manual al final
   */
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (onScrollToBottom) {
      onScrollToBottom();
    }
  };

  /**
   * Cargar más mensajes (scroll hacia arriba)
   */
  const handleLoadMore = () => {
    if (onLoadMore && !loadingMore && hasMore) {
      onLoadMore();
    }
  };

  // Estado inicial de carga
  if (loading && messages.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <Spinner animation="border" variant="primary" />
        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
          Cargando mensajes...
        </span>
      </div>
    );
  }

  // Error
  if (error && messages.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: '20px'
        }}
      >
        <Alert variant="danger" style={{ margin: 0, textAlign: 'center' }}>
          <strong>Error:</strong> {error}
        </Alert>
      </div>
    );
  }

  // Sin mensajes
  if (messages.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: '12px',
          color: '#6c757d'
        }}
      >
        <div style={{ fontSize: '3rem' }}>💬</div>
        <span style={{ fontSize: '0.95rem' }}>
          No hay mensajes en esta conversación
        </span>
      </div>
    );
  }

  return (
    <div
      className="messages-list-wrapper"
      style={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Botón cargar más mensajes (arriba) */}
      {hasMore && (
        <div
          style={{
            padding: '12px',
            textAlign: 'center',
            borderBottom: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}
        >
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{
              fontSize: '0.85rem',
              padding: '6px 16px'
            }}
          >
            {loadingMore ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Cargando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faChevronUp} className="me-2" />
                Cargar mensajes anteriores
              </>
            )}
          </Button>
        </div>
      )}

      {/* Container de mensajes con scroll */}
      <div
        ref={messagesContainerRef}
        className="messages-container"
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: compact ? '12px' : '16px',
          backgroundColor: '#fff',
          scrollBehavior: 'smooth'
        }}
      >
        {/* Lista de mensajes */}
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id || `msg-${index}`}
            message={message}
            isBot={message.sender === 'bot'}
            showTimestamp={true}
            compact={compact}
          />
        ))}

        {/* Referencia para scroll automático */}
        <div ref={messagesEndRef} />
      </div>

      {/* Botón scroll to bottom (flotante) */}
      {showScrollButton && (
        <Button
          variant="primary"
          size="sm"
          onClick={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 100,
            padding: 0
          }}
          className="scroll-to-bottom-btn"
          title="Ir al final"
        >
          <FontAwesomeIcon icon={faArrowDown} />
        </Button>
      )}

      <style>{`
        /* Scrollbar personalizado */
        .messages-container::-webkit-scrollbar {
          width: 8px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #c1c1c1, #a8a8a8);
          border-radius: 4px;
        }

        .messages-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #a8a8a8, #909090);
        }

        /* Animación del botón scroll */
        .scroll-to-bottom-btn {
          animation: scrollButtonFadeIn 0.3s ease-out;
          transition: all 0.2s ease;
        }

        .scroll-to-bottom-btn:hover {
          transform: scale(1.1);
        }

        .scroll-to-bottom-btn:active {
          transform: scale(0.95);
        }

        @keyframes scrollButtonFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Smooth scroll */
        .messages-container {
          scroll-behavior: smooth;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .messages-container {
            padding: 12px !important;
          }

          .scroll-to-bottom-btn {
            bottom: 12px !important;
            right: 12px !important;
            width: 36px !important;
            height: 36px !important;
          }
        }

        /* Prevenir selección accidental durante scroll rápido */
        .messages-container.scrolling {
          user-select: none;
        }

        /* Dark mode ready */
        @media (prefers-color-scheme: dark) {
          .messages-container {
            background-color: #1a1a1a;
          }

          .messages-container::-webkit-scrollbar-track {
            background: #2a2a2a;
          }

          .messages-container::-webkit-scrollbar-thumb {
            background: linear-gradient(45deg, #4a4a4a, #3a3a3a);
          }
        }
      `}</style>
    </div>
  );
};

export default MessagesList;
